import { ButtonLink } from "@lumi/ui-headless";
import { SubmissionCard as SharedSubmissionCard } from "../../../shared/components/SubmissionCard";
import { Checkbox } from "../../../shared/components/ui/checkbox";
import type { Submission } from "../hooks/useSubmissions";

interface SelectableSubmissionCardProps {
  readonly submission: Submission;
  readonly isSelected: boolean;
  readonly isSelectionMode: boolean;
  readonly onToggleSelect: (id: string) => void;
}

/**
 * SelectableSubmissionCard - Admin submission card with checkbox for bulk selection
 *
 * Displays submission information with:
 * - Selectable checkbox (visible in selection mode)
 * - Review action button
 * - Submitter information
 */
export function SelectableSubmissionCard({
  submission,
  isSelected,
  isSelectionMode,
  onToggleSelect,
}: SelectableSubmissionCardProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div className="flex items-start gap-3 group">
      {/* Selection Checkbox - only visible in selection mode (Pending tab) */}
      {isSelectionMode && (
        <div className="my-auto px-6">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(submission.id)}
            aria-label={`Select ${submission.title}`}
          />
        </div>
      )}

      {/* Card - flex-1 ensures it takes remaining width */}
      <div className="flex-1 min-w-0">
        <SharedSubmissionCard
          submission={submission}
          subtitle={
            <p className="text-sm text-neutral-500">
              Submitted by{" "}
              <span className="text-neutral-400">
                {submission.submitter.organization || submission.submitter.email}
              </span>{" "}
              on {formatDate(submission.submittedAt || submission.createdAt)}
            </p>
          }
          actions={
            <ButtonLink to={`/admin/review/${submission.id}`} variant="outline">
              Review
            </ButtonLink>
          }
        />
      </div>
    </div>
  );
}
